import React from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FieldWrapper from './FieldWrapper';
import { IField, ITradesValues, FieldType } from '../types';
import { useStyles } from '../styles';

interface ITradesSectionProps {
  fields: IField[];
  values: ITradesValues;
  errors: Record<string, any>;
  setValue: (key: keyof ITradesValues, value: any) => void;
  title?: string;
  required?: boolean;
  gridSpacing?: number;
  defaultGridSize?: number;
  isArchivedProject?: boolean;
}

export const TradesSection: React.FC<ITradesSectionProps> = ({
  fields,
  values,
  errors,
  setValue,
  title,
  required,
  gridSpacing = 3,
  defaultGridSize = 6,
  isArchivedProject,
}) => {
  const classes = useStyles();
  if (!fields.length) return null;

  return (
    <>
      {title && (
        <p className={classes.sectionTitle}>
          {title}
          {required && <span className={classes.requiredMark}>*</span>}
        </p>
      )}
      <Grid container spacing={gridSpacing}>
        {fields.map((field, index) => {
          if (!field.isVisible) return null;

          const gridSize = field.type === FieldType.Chips ? 12 : defaultGridSize;

          return (
            <Grid item xs={12} lg={gridSize} key={field.id}>
              <FieldWrapper
                field={field}
                index={index}
                value={values[field.id]}
                error={errors[field.id]?.message}
                disabled={isArchivedProject}
              />
            </Grid>
          );
        })}
      </Grid>
    </>
  );
};

interface IAddOtherTradeButtonProps {
  isOtherTradeSelected: boolean;
  onToggle: () => void;
  className?: string;
}

export const AddOtherTradeButton: React.FC<IAddOtherTradeButtonProps> = ({
  isOtherTradeSelected,
  onToggle,
  className = '',
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <div
      className={className}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      data-testid="add-other-trade-button"
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <span className={classes.plusIcon}>{isOtherTradeSelected ? '-' : '+'}</span>
      {t('trades.addOtherTrade')}
    </div>
  );
};
